import { Button, cn, toast } from "@ndd/ui";
import {
  Check,
  CircleAlert,
  CircleCheck,
  Info,
  Loader2,
  PencilLine,
  TriangleAlert,
} from "lucide-react";
import React, { type ReactElement, type ReactNode } from "react";
import type {
  SoapDraftProvenanceResponse,
  SoapDraftReviewResponse,
} from "../../../../shared/api-types";
import { HEALTHIE_CHART_NOTE_PATH_PATTERN } from "../../../../shared/healthie-chart-note";
import {
  healthieNoteClickThroughPath,
  SIGN_IN_HEALTHIE_LINK_TEXT,
  soapDraftDeliveryLine,
  type SoapDraftDelivery,
  type SoapDraftDeliveryTone,
} from "../../lib/soap-draft-delivery";
import { clockOrDateLabel, draftProvenanceLine } from "../../lib/soap-draft-provenance";
import { AiMarkerGlyph, AiMarkerLabel } from "../v2/ai-marker";
import { useNewerHealthieTranscript } from "./draft-transcript-notice";
import { EditorFooter } from "./editor-status";
import type {
  AppointmentDraftAutosaveUiState,
  SoapDraftApprovalActionOutcome,
} from "./use-appointment-draft-autosave";

interface SoapDraftFooterProps {
  appointmentId?: string;
  review: SoapDraftReviewResponse;
  provenance: SoapDraftProvenanceResponse;
  snapshot: AppointmentDraftAutosaveUiState;
  approving: boolean;
  /** Approved and not yet unlocked for further edits. */
  locked: boolean;
  /** NO-785: a Re-enhance is running, so Approve waits. */
  approveBlocked?: boolean;
  now: string;
  timezone: string;
  copyAction: ReactNode;
  delivery?: SoapDraftDelivery;
  /** The snapshot store lost its connection: the delivery line is the last known state. */
  reconnecting?: boolean;
  onApprove: () => Promise<SoapDraftApprovalActionOutcome>;
  onUnlock: () => void;
}

/**
 * Pinned under the draft text: where it came from, then Copy and Approve.
 * Approval sits at the bottom on purpose — after the note, not before it.
 *
 * Approve names the exact saved version, so it waits while a save is in
 * flight or text is unsaved; the toolbar status next to it says why. An
 * approved draft locks its body; "Make changes" unlocks it, and the first
 * material save needs a fresh approval (the server drops the old one).
 *
 * Under the approval line, an approved draft says what happened to it in
 * Healthie (NO-541): sending, sent, a collision, or a failure. `delivery` is
 * `undefined` on a surface with no documentation snapshot, which shows no line.
 * `appointmentId` lets a delivered line link to the note in Healthie (NO-679).
 */
export function SoapDraftFooter({
  appointmentId,
  review,
  provenance,
  snapshot,
  approving,
  locked,
  approveBlocked = false,
  now,
  timezone,
  copyAction,
  delivery,
  reconnecting = false,
  onApprove,
  onUnlock,
}: SoapDraftFooterProps): ReactElement {
  const approved = review.state === "approved";
  const approvable = review.state === "draft" && review.approval === "available";
  const approveWaits = [
    approving,
    snapshot.dirty,
    snapshot.status !== "saved",
    approveBlocked,
  ].some(Boolean);

  return (
    <EditorFooter>
      <SoapDraftFooterRow
        status={
          <FooterStatusLines
            review={review}
            provenance={provenance}
            dirty={snapshot.dirty}
            now={now}
            timezone={timezone}
            delivery={delivery}
            reconnecting={reconnecting}
            appointmentId={appointmentId}
          />
        }
        actions={
          <FooterActions
            copyAction={copyAction}
            showUnlock={approved && locked}
            showApprove={approvable}
            approveDisabled={approveWaits}
            approving={approving}
            onApprove={onApprove}
            onUnlock={onUnlock}
          />
        }
      />
    </EditorFooter>
  );
}

/**
 * One footer row for the RD's editor and the read-only view alike: the
 * provenance and status lines stack on the left, the actions sit on the right
 * (NO-732). A lone Copy note therefore sits beside the status instead of on a
 * row of its own. The actions align to the bottom, after the note's last
 * status line; the status column's bottom padding centers a one-line status
 * on the buttons. On a narrow card the actions wrap under the status and stay
 * on the right; the actions group can shrink, so on a very narrow card its
 * buttons wrap onto their own lines instead of being clipped.
 */
export function SoapDraftFooterRow({
  status,
  actions,
}: {
  status: ReactNode;
  actions: ReactNode;
}): ReactElement {
  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
      <div
        data-footer-status=""
        className="flex min-w-0 flex-1 basis-64 flex-col gap-2.5 pb-1.5"
      >
        {status}
      </div>
      <div
        data-footer-actions=""
        className="ml-auto flex flex-wrap items-center justify-end gap-2"
      >
        {actions}
      </div>
    </div>
  );
}

/** Provenance first, then whatever the review state has to say. */
function FooterStatusLines({
  review,
  provenance,
  dirty,
  now,
  timezone,
  delivery,
  reconnecting,
  appointmentId,
}: {
  review: SoapDraftReviewResponse;
  provenance: SoapDraftProvenanceResponse;
  dirty: boolean;
  now: string;
  timezone: string;
  delivery: SoapDraftDelivery | undefined;
  reconnecting: boolean;
  /** Lets a delivered line link to the note in Healthie (NO-679). */
  appointmentId: string | undefined;
}): ReactElement {
  return (
    <>
      <ProvenanceLine provenance={provenance} now={now} timezone={timezone} />
      {review.state === "approved" ? (
        <ApprovedLine review={review} dirty={dirty} now={now} timezone={timezone} />
      ) : null}
      {review.state === "approved" && delivery !== undefined ? (
        <SoapDraftDeliveryStatus
          delivery={delivery}
          reconnecting={reconnecting}
          {...(appointmentId === undefined ? {} : { appointmentId })}
        />
      ) : null}
      {review.state === "draft" && review.approval === "unavailable" ? (
        <ApprovalUnavailableLine />
      ) : null}
    </>
  );
}

/** Copy note first, then Make changes or Approve note — never both. */
function FooterActions({
  copyAction,
  showUnlock,
  showApprove,
  approveDisabled,
  approving,
  onApprove,
  onUnlock,
}: {
  copyAction: ReactNode;
  showUnlock: boolean;
  showApprove: boolean;
  approveDisabled: boolean;
  approving: boolean;
  onApprove: () => Promise<SoapDraftApprovalActionOutcome>;
  onUnlock: () => void;
}): ReactElement {
  return (
    <>
      {copyAction}
      {showUnlock ? (
        <Button type="button" variant="utility" size="compact" onClick={onUnlock}>
          <PencilLine aria-hidden="true" />
          Make changes
        </Button>
      ) : null}
      {showApprove ? (
        <ApproveButton
          disabled={approveDisabled}
          approving={approving}
          onApprove={onApprove}
        />
      ) : null}
    </>
  );
}

function ProvenanceLine({
  provenance,
  now,
  timezone,
}: {
  provenance: SoapDraftProvenanceResponse;
  now: string;
  timezone: string;
}): ReactElement {
  const newerHealthieTranscript = useNewerHealthieTranscript();
  const line = draftProvenanceLine(provenance, {
    now,
    timezone,
    newerHealthieTranscript,
  });

  return <DraftProvenanceSentence line={line} />;
}

/** NO-775: the provenance sentence and, when a newer Healthie transcript arrived, its notice. */
export function DraftProvenanceSentence({
  line,
}: {
  line: ReturnType<typeof draftProvenanceLine>;
}): ReactElement {
  return (
    <p className="flex items-start gap-2 text-body-sm leading-[18px]">
      <AiMarkerGlyph />
      <span>
        <AiMarkerLabel>{line.generated}</AiMarkerLabel>
        <span className="text-muted-foreground">{line.sources}</span>
        {line.notice ? (
          <span className="text-muted-foreground"> {line.notice}</span>
        ) : null}
      </span>
    </p>
  );
}

function ApprovedLine({
  review,
  dirty,
  now,
  timezone,
}: {
  review: Extract<SoapDraftReviewResponse, { state: "approved" }>;
  dirty: boolean;
  now: string;
  timezone: string;
}): ReactElement {
  return (
    <p role="status" className="flex items-start gap-2 text-body-sm leading-[18px]">
      <CircleCheck
        aria-hidden="true"
        className="mt-0.5 size-3.5 shrink-0 text-success"
      />
      <span>
        <span className="font-medium text-foreground">
          Approved {clockOrDateLabel(review.approval.approvedAt, { now, timezone })}
        </span>
        {dirty ? (
          <span className="text-muted-foreground">
            {" "}
            · your changes will need a new approval.
          </span>
        ) : null}
      </span>
    </p>
  );
}

function ApprovalUnavailableLine(): ReactElement {
  return (
    <p
      role="status"
      className="flex items-start gap-2 text-body-sm leading-[18px] text-muted-foreground"
    >
      <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <span>
        This session isn&apos;t linked to a Healthie appointment, so the note can&apos;t
        be approved here. Copy it into Healthie instead.
      </span>
    </p>
  );
}

function ApproveButton({
  disabled,
  approving,
  onApprove,
}: {
  disabled: boolean;
  approving: boolean;
  onApprove: () => Promise<SoapDraftApprovalActionOutcome>;
}): ReactElement {
  async function handleApprove(): Promise<void> {
    const outcome = await onApprove();
    const feedback = soapDraftApprovalFeedback(outcome);
    if (!feedback) return;

    if (feedback.kind === "success") toast.success(feedback.message);
    else toast.error(feedback.message);
  }

  return (
    <Button
      type="button"
      variant="press"
      size="compact"
      disabled={disabled}
      onClick={() => void handleApprove()}
    >
      <Check aria-hidden="true" />
      {approving ? "Approving…" : "Approve note"}
    </Button>
  );
}

const DELIVERY_TONE_CLASSES: Record<SoapDraftDeliveryTone, string> = {
  neutral: "text-muted-foreground",
  success: "text-success",
  danger: "text-danger",
  warning: "text-warning",
};

function DeliveryIcon({
  tone,
  sending,
}: {
  tone: SoapDraftDeliveryTone;
  sending: boolean;
}): ReactElement {
  const className = cn("mt-0.5 size-3.5 shrink-0", DELIVERY_TONE_CLASSES[tone]);
  // The spinner keeps spinning under reduced motion: it is status, not decoration.
  if (sending)
    return <Loader2 aria-hidden="true" className={cn(className, "animate-spin")} />;

  if (tone === "success")
    return <CircleCheck aria-hidden="true" className={className} />;

  if (tone === "danger")
    return <CircleAlert aria-hidden="true" className={className} />;

  if (tone === "warning")
    return <TriangleAlert aria-hidden="true" className={className} />;

  return <Info aria-hidden="true" className={className} />;
}

/**
 * One line, one closed state: the headline in the tone's color, the next step
 * in plain grey. Shared by the RD's editor footer and the admin read-only view.
 *
 * A delivered line links "sign it in Healthie" to the click-through route only
 * when the shared chart-note pattern is set (`healthieNoteLinkAvailable`) and
 * the surface knows its Appointment. Otherwise the markup is today's plain text.
 */
export function SoapDraftDeliveryStatus({
  delivery,
  reconnecting = false,
  appointmentId,
  rdId,
  healthieNoteLinkAvailable = HEALTHIE_CHART_NOTE_PATH_PATTERN !== null,
}: {
  delivery: SoapDraftDelivery;
  /** Shows the same "connection lost" cue the note footer shows, under the last known state. */
  reconnecting?: boolean;
  appointmentId?: string;
  /** The selected RD on the admin read-only view; absent on the RD's own lane. */
  rdId?: string;
  healthieNoteLinkAvailable?: boolean;
}): ReactElement {
  const line = soapDraftDeliveryLine(delivery);
  const sending = delivery !== null && delivery.state === "in_progress";
  const healthieNoteHref = deliveredNoteHref({
    available: healthieNoteLinkAvailable,
    appointmentId,
    rdId,
    delivery,
  });

  return (
    <>
      <p
        role="status"
        data-delivery-state={delivery === null ? "not_sent" : delivery.state}
        className="flex items-start gap-2 text-body-sm leading-[18px]"
      >
        <DeliveryIcon tone={line.tone} sending={sending} />
        <span>
          <span
            className={cn(
              "font-medium",
              line.tone === "neutral"
                ? "text-foreground"
                : DELIVERY_TONE_CLASSES[line.tone]
            )}
          >
            {line.headline}
          </span>
          {healthieNoteHref !== null ? (
            <DeliveredDetailWithLink detail={line.detail} href={healthieNoteHref} />
          ) : (
            <span className="text-muted-foreground"> {line.detail}</span>
          )}
        </span>
      </p>
      {reconnecting ? (
        <p className="text-xs text-warning" role="status" data-delivery-stale="true">
          Connection lost. Showing the last known status and retrying…
        </p>
      ) : null}
    </>
  );
}

function deliveredNoteHref(input: {
  available: boolean;
  appointmentId: string | undefined;
  rdId: string | undefined;
  delivery: SoapDraftDelivery;
}): string | null {
  if (!input.available || input.appointmentId === undefined) return null;

  if (input.delivery?.state !== "delivered") return null;

  return healthieNoteClickThroughPath(input.appointmentId, input.rdId);
}

function DeliveredDetailWithLink({
  detail,
  href,
}: {
  detail: string;
  href: string;
}): ReactElement {
  const linkStart = detail.indexOf(SIGN_IN_HEALTHIE_LINK_TEXT);
  if (linkStart < 0) return <span className="text-muted-foreground"> {detail}</span>;

  return (
    <span className="text-muted-foreground">
      {` ${detail.slice(0, linkStart)}`}
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline"
      >
        {SIGN_IN_HEALTHIE_LINK_TEXT}
      </a>
      {detail.slice(linkStart + SIGN_IN_HEALTHIE_LINK_TEXT.length)}
    </span>
  );
}

export function soapDraftApprovalFeedback(
  outcome: SoapDraftApprovalActionOutcome
): { kind: "success" | "error"; message: string } | null {
  switch (outcome) {
    case "approved":
      return { kind: "success", message: "Note approved" };
    case "conflict":
      return {
        kind: "error",
        message: "The draft changed. Review the latest version before approving.",
      };
    case "refused":
      return { kind: "error", message: "This draft can't be approved yet." };
    case "re_enhancing":
      return {
        kind: "error",
        message: "A new draft is being made. You can approve it when it's ready.",
      };
    case "superseded":
      return null;
    case "unavailable":
      return { kind: "error", message: "Couldn't approve the note. Try again." };
  }
}
